/**
 * 遊ばれ方の記録（自前の軽い計測。2026-10-04 相島さん決定：Firebase ではなく Cloudflare で）。
 *
 * - 出来事は端末の中にためて、結果画面・画面が隠れたとき・ある程度たまったときに、まとめて送る
 * - 送り先は Cloudflare Pages Functions（functions/api/events.ts）→ D1
 * - 送れなくても遊びには何も影響しない。ためておく数と、送り直す回数には上限がある
 * - 設定の「遊び方の記録を送る」を切ると、ためていた分も捨てて、以後は何もしない
 * - 名前・メール・広告IDは送らない。端末ID はランキングと同じランダムな文字列
 */

import type { StorageLike } from '../game/systems/StorageSystem';
import { detectStorage } from '../game/systems/StorageSystem';
import {
  TELEMETRY_MAX_BATCH,
  type TelemetryBatch,
  type TelemetryEvent,
  type TelemetryEventType,
  type TelemetryPlatform,
} from './telemetrySchema';

/** 端末にためておく最大件数。超えたら古いものから捨てる。 */
export const TELEMETRY_MAX_QUEUE = 300;
/** 1件を送り直す最大回数。超えたら捨てる。 */
export const TELEMETRY_MAX_TRIES = 5;
/** これだけたまったら、結果画面を待たずに送る。 */
export const TELEMETRY_AUTO_FLUSH = 30;

interface StoredEvent extends TelemetryEvent {
  /** 送ろうとして失敗した回数。 */
  tries?: number;
}

interface StoredState {
  queue: StoredEvent[];
  /** 「初めてのプレイ」をもう記録したか。 */
  firstPlay: boolean;
  /** 最後に launch を記録した日（日本時間 YYYY-MM-DD）。アプリの復帰で日が変わったかを見る。 */
  lastLaunchDay: string;
}

export type TelemetrySend = (batch: TelemetryBatch) => Promise<boolean>;

export interface TelemetryOptions {
  storageKey: string;
  storage?: StorageLike | null;
  /** 端末ID（ランキングの player ID）。 */
  device: () => string;
  platform: TelemetryPlatform;
  app: string;
  /** 設定で「送る」になっているか。毎回読む（切り替えをすぐ反映するため）。 */
  enabled: () => boolean;
  send: TelemetrySend;
  now?: () => number;
}

function normalize(raw: unknown): StoredState {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const queue = Array.isArray(r.queue)
    ? (r.queue.filter((e) => e && typeof e === 'object' && typeof (e as StoredEvent).type === 'string') as StoredEvent[])
    : [];
  return {
    queue: queue.slice(-TELEMETRY_MAX_QUEUE),
    firstPlay: r.firstPlay === true,
    lastLaunchDay: typeof r.lastLaunchDay === 'string' ? r.lastLaunchDay : '',
  };
}

export class Telemetry {
  private readonly storage: StorageLike | null;
  private state: StoredState;
  private sending: Promise<void> | null = null;

  constructor(private readonly options: TelemetryOptions) {
    this.storage = options.storage !== undefined ? options.storage : detectStorage();
    this.state = this.read();
  }

  get enabled(): boolean {
    try {
      return this.options.enabled();
    } catch {
      return false;
    }
  }

  /** ためている件数（テスト・確認用）。 */
  get pending(): number {
    return this.state.queue.length;
  }

  /** 出来事を1件ためる。切っているときは何もしない。 */
  track(type: TelemetryEventType, fields: Omit<TelemetryEvent, 'type' | 'at'> = {}): void {
    if (!this.enabled) return;
    const event: StoredEvent = { type, at: Math.round(this.now()) };
    for (const [key, value] of Object.entries(fields)) {
      if (value === undefined || value === null) continue;
      (event as unknown as Record<string, unknown>)[key] = value;
    }
    this.state.queue.push(event);
    if (this.state.queue.length > TELEMETRY_MAX_QUEUE) {
      this.state.queue.splice(0, this.state.queue.length - TELEMETRY_MAX_QUEUE);
    }
    this.write();
    if (this.state.queue.length >= TELEMETRY_AUTO_FLUSH) void this.flush();
  }

  /** 初めてのプレイなら true を返して印を付ける（2回目からは false）。 */
  markFirstPlay(): boolean {
    if (this.state.firstPlay) return false;
    this.state.firstPlay = true;
    this.write();
    return true;
  }

  /** 最後に launch を記録した日（日本時間）。まだなら空文字。 */
  get lastLaunchDay(): string {
    return this.state.lastLaunchDay;
  }

  /** launch を記録した日を覚える。 */
  noteLaunchDay(day: string): void {
    if (this.state.lastLaunchDay === day) return;
    this.state.lastLaunchDay = day;
    this.write();
  }

  /** 設定で切られたとき：ためていた分を捨てる。 */
  discard(): void {
    this.state.queue = [];
    this.write();
  }

  /**
   * ためている分を送る（50件ずつ）。失敗したら残して、次の機会に送り直す。
   * 送っている最中に呼ばれたら、その送信が終わるのを待つだけ。
   */
  flush(): Promise<void> {
    if (this.sending) return this.sending;
    this.sending = this.flushAll().finally(() => {
      this.sending = null;
    });
    return this.sending;
  }

  private async flushAll(): Promise<void> {
    if (!this.enabled) {
      if (this.state.queue.length > 0) this.discard();
      return;
    }
    // 1回の flush で送るのは数回まで（たまりすぎていても、画面が隠れる短い間に終わるように）
    for (let round = 0; round < 4 && this.state.queue.length > 0; round += 1) {
      const chunk = this.state.queue.slice(0, TELEMETRY_MAX_BATCH);
      let device: string;
      try {
        device = this.options.device();
      } catch {
        return;
      }
      const batch: TelemetryBatch = {
        device,
        platform: this.options.platform,
        app: this.options.app,
        events: chunk.map(({ tries: _tries, ...rest }) => rest),
      };
      let ok = false;
      try {
        ok = await this.options.send(batch);
      } catch {
        ok = false;
      }
      // 送っている間に新しくたまった分は残す（先頭の chunk ぶんだけ扱う）
      const sent = new Set(chunk);
      if (ok) {
        this.state.queue = this.state.queue.filter((e) => !sent.has(e));
        this.write();
        continue;
      }
      for (const e of chunk) e.tries = (e.tries ?? 0) + 1;
      this.state.queue = this.state.queue.filter((e) => (e.tries ?? 0) < TELEMETRY_MAX_TRIES);
      this.write();
      return;
    }
  }

  private now(): number {
    return this.options.now ? this.options.now() : Date.now();
  }

  private read(): StoredState {
    try {
      const raw = this.storage?.getItem(this.options.storageKey);
      return normalize(raw ? JSON.parse(raw) : null);
    } catch {
      return normalize(null);
    }
  }

  private write(): void {
    try {
      this.storage?.setItem(this.options.storageKey, JSON.stringify(this.state));
    } catch {
      /* 容量超過など。メモリの上だけで続ける */
    }
  }
}

/* ---------------- どこからでも呼べる窓口 ----------------
 * 画面の部品（共有ボタン・ショップなど）から `track('share', …)` と1行で呼べるように。
 * まだ用意されていない（テスト・計測を入れていない画面）ときは何もしない。 */

let current: Telemetry | null = null;
/** 計測を始める前に起きた出来事（trackEarly）。始めたときに入れる。 */
let early: Array<{ type: TelemetryEventType; fields?: Omit<TelemetryEvent, 'type' | 'at'> }> = [];

export function setTelemetry(telemetry: Telemetry | null): void {
  current = telemetry;
  if (!telemetry || early.length === 0) return;
  const list = early;
  early = [];
  for (const e of list) track(e.type, e.fields);
}

/**
 * まだ計測を始めていなくても落とさずに記録する（お知らせから開いた、など起動の途中で起きるもの）。
 * 始まっていなければ覚えておき、setTelemetry のときに入れる。
 */
export function trackEarly(type: TelemetryEventType, fields?: Omit<TelemetryEvent, 'type' | 'at'>): void {
  if (current) {
    track(type, fields);
    return;
  }
  if (early.length < 10) early.push({ type, fields });
}

export function getTelemetry(): Telemetry | null {
  return current;
}

export function track(type: TelemetryEventType, fields?: Omit<TelemetryEvent, 'type' | 'at'>): void {
  try {
    current?.track(type, fields);
  } catch {
    /* 計測の失敗で遊びを止めない */
  }
}

export function flushTelemetry(): void {
  try {
    void current?.flush();
  } catch {
    /* noop */
  }
}
